import React, { useCallback, useState } from 'react';
import * as XLSX from "xlsx";
import useCustomToast from "../../hooks/use-toast";
import { usePost } from "../../hooks/api-call";
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Button, Paper, LinearProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const FileUpload = ({isTrips}) => {
    const { postData } = usePost();
    const { showSuccessToast, showErrorToast } = useCustomToast();
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [fileName, setFileName] = useState('');

    const validateTripSheet = (jsonData) => {
        const requiredFields = [
            "truckNumber", "erawannaNo", "challanNo", "orderId",
            "driverName", "driverMobileNumber", "sellerTripQuantity",
            "buyerTripQuantity", "tds", "paymentTo"
        ];
    
        const headers = Object.keys(jsonData[0] || {});
        const missingHeaders = requiredFields.filter(f => !headers.includes(f));
    
        if (missingHeaders.length > 0) {
            showErrorToast("error", `Missing fields in sheet: ${missingHeaders.join(", ")}`);
            return false;
        }
    
        const invalidRows = jsonData.filter(row =>
            requiredFields.some(field => !row[field] || row[field].toString().trim() === "")
        );
    
        if (invalidRows.length > 0) {
            showErrorToast("error", `Sheet has ${invalidRows.length} rows with missing required data.`);
            return false;
        }
    
        const gstInvalidRows = jsonData.filter((row, index) => {
            const igst = row.igst?.toString().trim();
            const cgst = row.cgst?.toString().trim();
            const sgst = row.sgst?.toString().trim();
    
            const hasIGST = igst && igst !== "0";
            const hasCGST_SGST = cgst && cgst !== "0" && sgst && sgst !== "0";
    
            return !(hasIGST || hasCGST_SGST);
        });
    
        if (gstInvalidRows.length > 0) {
            showErrorToast("error", `Sheet has ${gstInvalidRows.length} rows with missing IGST or CGST+SGST values.`);
            return false;
        }
    
        return true;
    };
    
    const validateTruckSheet = (jsonData) => {
        const requiredFields = ["truckNumber"];
    
        const headers = Object.keys(jsonData[0] || {});
        const missingHeaders = requiredFields.filter(f => !headers.includes(f));
    
        if (missingHeaders.length > 0) {
            showErrorToast("error", `Missing fields in sheet: ${missingHeaders.join(", ")}`);
            return false;
        }
    
        const invalidRows = jsonData.filter(row =>
            requiredFields.some(field => !row[field] || row[field].toString().trim() === "")
        );
    
        if (invalidRows.length > 0) {
            showErrorToast("error", `Sheet has ${invalidRows.length} rows with missing required data.`);
            return false;
        }
    
        return true;
    }

    const chunkArray = (array, size) => {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    };

    const uploadChunksToAPI = async (jsonData) => {
        setUploading(true);
        const chunkSize = 1000;
        const chunks = chunkArray(jsonData, chunkSize);

        for (let i = 0; i < chunks.length; i++) {
            const chunk = chunks[i];
            try {
                await postData(chunk, isTrips ? `/bulk/trip` : `/bulk/truck`);
                const progressPercent = Math.round(((i + 1) / chunks.length) * 100);
                setProgress(progressPercent);
            } catch (error) {
                console.error("Error uploading chunk:", error);
                showErrorToast("error", `Failed to upload chunk ${i + 1}`);
            }
        }

        setUploading(false);
        showSuccessToast("Success", `${isTrips ? 'Trips' : 'Trucks'} data uploaded successfully.`);
        setProgress(0);
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (!acceptedFiles || acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        if (!file) return;

        setFileName(file.name);
        const reader = new FileReader();

        reader.onload = (evt) => {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: "array" });
        
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
        
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
        
            let hasInvalidDate = false; // Flag to track any invalid date
        
            const formattedData = jsonData.map((row, index) => {
                let generatedOn = row.generatedOn;
                if (typeof generatedOn === "number") {
                    const parsed = XLSX.SSF.parse_date_code(generatedOn);
                    if (parsed) {
                        const dateObj = new Date(parsed.y, parsed.m - 1, parsed.d, parsed.H, parsed.M, parsed.S);
                        if (!isNaN(dateObj)) {
                            row.generatedOn = dateObj.toISOString();
                        } else {
                            console.error(`Invalid Excel serial date at row ${index + 2}: ${generatedOn}`);
                            hasInvalidDate = true;
                        }
                    } else {
                        console.error(`Unable to parse Excel serial at row ${index + 2}: ${generatedOn}`);
                        hasInvalidDate = true;
                    }
                } else if (typeof generatedOn === "string") {
                    const cleaned = generatedOn.replace(/\s+/g, ' ').trim();
        
                    const dateObj = new Date(cleaned);
                    if (!isNaN(dateObj)) {
                        row.generatedOn = generatedOn;
                    } else {
                        console.error(`Invalid string date at row ${index + 2}: ${generatedOn}`);
                        showErrorToast("error", `Invalid date string: ${generatedOn} (Row ${index + 2})`);
                        hasInvalidDate = true;
                    }
                }
        
                return row;
            });
             
            if(isTrips) {
               if (!hasInvalidDate && validateTripSheet(formattedData)) {
                uploadChunksToAPI(formattedData);
            } else {
                console.warn("Upload skipped due to invalid dates.");
            } 
            }
            else {
                if (validateTruckSheet(formattedData)) {
                    uploadChunksToAPI(formattedData);
                } else {
                    console.warn("Upload skipped due to validation errors.");
                }
            }
        };

        reader.readAsArrayBuffer(file);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#f9f9f9',
                p: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: 400,
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 2,
                }}
            >
                {!uploading && (
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed #ccc',
                            p: 3,
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: '0.3s',
                            '&:hover': { borderColor: '#1976d2' },
                            bgcolor: isDragActive ? '#e3f2fd' : '#fff',
                        }}
                    >
                        <input {...getInputProps()} />
                        <CloudUploadIcon sx={{ fontSize: 50, color: '#1976d2' }} />
                        <Typography variant="h6" mt={2}>
                            {isDragActive
                                ? 'Drop the file here...'
                                : 'Drag & drop or click to upload'}
                        </Typography>
                        <Button variant="contained" sx={{ mt: 3 }}>
                            Browse Files
                        </Button>
                    </Box>
                )}

                {uploading && (
                    <Box>
                        <Typography variant="h6" mb={2}>
                            Uploading: {fileName}
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} />
                        <Typography mt={2}>{progress}%</Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default FileUpload;
